import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  GridRowModes,
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import {
  Box,
  Button,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import DatasetIcon from "@mui/icons-material/Dataset";
import FilterAlt from "@mui/icons-material/FilterAlt";
import FilterAltOff from "@mui/icons-material/FilterAltOff";
import IconButton from "@mui/material/IconButton";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PersonIcon from "@mui/icons-material/Person";
import TaskIcon from "@mui/icons-material/Task";
import WaterIcon from "@mui/icons-material/Water";

import axios from "axios";
import styled from "@emotion/styled";

import ExportData from "./ExportData";
import Filter from "./Filter";
import { dataActions, dataExplorerActions } from "../../store";
import { entities } from "../../config/config.dataExplorer";
import { API_URL, LIMIT } from "../../config/config.data";
import { getData } from "../../store/dataSlice";
import { AddNwisRecord } from "./AddNwisRecord";
import { AddScienceBaseRecord } from "./AddScienceBaseRecord";

// customize the search bar component
const CustomGridToolbarQuickFilter = styled(GridToolbarQuickFilter)(
  ({ theme }) => ({
    border: `1px solid ${theme.palette.primary.dark}`,
    padding: "3px",
    borderRadius: "5px",
    width: "300px",
    "& .MuiInputBase-input": { padding: 0 },
    "& .MuiInput-underline:before": {
      borderBottom: 0,
    },
    "& .MuiInput-underline:after": {
      borderBottom: 0,
    },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
      borderBottom: 0,
    },
  })
);

// adapted from https://mui.com/x/react-data-grid/components/#toolbar
export default function Toolbar({
  setRowModesModel,
  setSnackbarAutoHideDuration,
  setSnackbarOpen,
  setSnackbarMessage,
  editable,
  showFilters,
}) {
  const dispatch = useDispatch();
  const selectedEntityId = useSelector(
    (state) => state.dataExplorer.selectedEntity
  );
  const filters = useSelector(
    (state) => state.dataExplorer.filters[selectedEntityId]
  );
  const [filtersExpanded, setFiltersExpanded] = useState(showFilters);
  const entity = entities[selectedEntityId];

  // define filtersActive to determine whether to show the Clear filters button
  const filtersActive = Object.keys(filters).some(
    (filter) => filters[filter].length > 0
  );

  const iconStyle = {
    color: "primary.dark",
  };

  const mainResources = [
    { id: "basins", icon: <WaterIcon sx={iconStyle} /> },
    { id: "datasets", icon: <DatasetIcon sx={iconStyle} /> },
    { id: "people", icon: <PersonIcon sx={iconStyle} /> },
    { id: "projects", icon: <TaskIcon sx={iconStyle} /> },
    { id: "sites", icon: <LocationOnIcon sx={iconStyle} /> },
  ];
  const mainResourceIds = mainResources.map((resource) => resource.id);
  const allResourceOptions = Object.keys(entities).map((entity) => ({
    value: entities[entity].id,
    label: entities[entity].label,
  }));

  const mainResourceOptions = allResourceOptions
    .filter((option) => mainResourceIds.includes(option.value))
    .map((option) => ({
      ...option,
      icon: mainResources.find((resource) => resource.id === option.value).icon,
    }));

  const metadataResourceOptions = allResourceOptions.filter(
    (option) => !mainResourceIds.includes(option.value)
  );

  const filtersAvailable = entity?.filterOptions?.length > 0;

  const handleAddRecord = () => {
    // clear filters
    handleClearFilters();
    let body = {};
    // check if the entity has required fields for adding a record
    // if not, body should just be an object with name as a key and an empty string as the value
    const requiredFields = entity.requiredCreateFields;
    if (requiredFields?.length > 0) {
      requiredFields.forEach((field) => {
        body[field] = "";
      });
    } else {
      body = {
        name: "",
      };
    }

    setSnackbarAutoHideDuration(null);
    setSnackbarOpen(true);
    setSnackbarMessage("Adding...");

    // post new record to API, then get updated data from API and dispatch to store
    // get the largest pk (the new record) and set its row mode to edit to allow editing
    axios
      .post(`${API_URL}/${entity.endpoint}`, body)
      .then((response) => {
        axios
          .get(`${API_URL}/${entity.endpoint}?limit=${LIMIT}`)
          .then((response) => {
            const key = entity.responseBodyKey || entity.endpoint;
            const data = response.data[key];
            const id = data.sort((a, b) => b.id - a.id)[0].id;
            dispatch(dataActions.setData({ data, dataName: selectedEntityId }));
            if (selectedEntityId === "sites") {
              dispatch(dataActions.setFilteredSiteData(data));
            }
            setRowModesModel((oldModel) => ({
              ...oldModel,
              [id]: { mode: GridRowModes.Edit, fieldToFocus: "Field" },
            }));
            setSnackbarOpen(false);
            setSnackbarMessage("");
          })
          .catch((error) => {
            setSnackbarMessage("Failed to fetch data after adding record");
            setSnackbarAutoHideDuration(5000);
            console.log(error);
          });
      })
      .catch((error) => {
        if (error.response && error.response.status === 400) {
          const errorDetail = error.response.data.detail || "Bad Request";
          setSnackbarMessage(`Failed to add record: ${errorDetail}`);
        } else {
          setSnackbarMessage("Failed to add record");
        }
        setSnackbarAutoHideDuration(5000);
        setSnackbarOpen(true);
        console.log(error);
      });
  };

  const handleClearFilters = () => {
    dispatch(dataExplorerActions.clearFilters());
  };

  const handleChangeResource = (event) => {
    dispatch(dataExplorerActions.setSelectedEntity(event.target.value));
    dispatch(getData({ dataId: event.target.value, source: "Toolbar" }));
  };

  return (
    <GridToolbarContainer sx={{ pt: 1, backgroundColor: "primary.light" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",

            gap: 1,
            my: 1,
            flexWrap: "wrap",
          }}
        >
          <Grid
            container
            spacing={2}
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Grid
              item
              xs={12}
              md={4}
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "flex-start",
              }}
            >
              <Box>
                <CustomGridToolbarQuickFilter />
              </Box>
            </Grid>
            <Grid
              item
              xs={8}
              md={4}
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: { xs: "start", md: "center" },
              }}
            >
              {editable ? (
                <Box
                  id="resource-select-container"
                  sx={{
                    width: "100%",
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                    justifyContent: {
                      xs: "flex-start",
                      md: "center",
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      visibility: { xs: "hidden", md: "visible" },
                      display: { xs: "none", md: "block" },
                    }}
                  >
                    Select a resource:
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel id="resource-select-label">Resource</InputLabel>
                    <Select
                      labelId="resource-select-label"
                      id="resource-select"
                      label="Resource"
                      value={selectedEntityId}
                      onChange={handleChangeResource}
                      sx={{ minWidth: "200px" }}
                    >
                      {mainResourceOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          sx={{
                            "&.Mui-selected": {
                              backgroundColor: "primary.main",
                              color: "primary.contrastText",
                            },
                          }}
                        >
                          {option.icon ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                              }}
                            >
                              {option.icon}
                              {option.label}
                            </Box>
                          ) : (
                            option.label
                          )}
                        </MenuItem>
                      ))}

                      <Divider />
                      {metadataResourceOptions.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          dense
                          sx={{
                            "&.Mui-selected": {
                              backgroundColor: "primary.main",
                              color: "primary.contrastText",
                            },
                          }}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              ) : null}
            </Grid>
            {
              <Grid
                item
                xs={4}
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                {filtersAvailable ? (
                  <IconButton
                    color="secondary"
                    onClick={() => {
                      setFiltersExpanded(!filtersExpanded);
                    }}
                  >
                    {filtersExpanded ? (
                      <FilterAltOff sx={{ fontSize: "1.75rem" }} />
                    ) : (
                      <FilterAlt sx={{ fontSize: "1.75rem" }} />
                    )}
                  </IconButton>
                ) : null}
              </Grid>
            }
          </Grid>

          {filtersExpanded && filtersAvailable ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
                my: 1,
                flexWrap: "wrap",
              }}
            >
              {entity?.filterOptions?.map((filter) => (
                <Filter key={filter.id} id={filter.id} />
              ))}
              {filtersActive ? (
                <Button
                  variant="outlined"
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon sx={{ color: "accent.main" }} />}
                  sx={{
                    color: "primary.contrastText",
                    height: "30px",
                    fontSize: "0.8rem",
                    "&:hover": {
                      borderColor: "primary.dark",
                    },
                    // borderWidth: "2px",
                    borderRadius: "15px",
                    borderColor: "accent.main",
                  }}
                >
                  Clear filters
                </Button>
              ) : null}
            </Box>
          ) : null}
        </Box>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          {editable ? (
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddRecord}
              sx={{
                color: "primary.contrastText",
                borderColor: "primary.dark",
                "&:hover": {
                  borderColor: "primary.dark",
                },
              }}
            >
              Add record
            </Button>
          ) : null}
          {selectedEntityId === "sites" ? (
            <AddNwisRecord />
          ) : selectedEntityId === "datasets" ? (
            <AddScienceBaseRecord />
          ) : null}
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {editable ? (
            <Typography variant="body2">
              Double click on a row to edit
            </Typography>
          ) : null}
        </Grid>
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
        >
          <ExportData filename={entity.label} />
        </Grid>
      </Grid>
    </GridToolbarContainer>
  );
}
